import { useAppConfig } from 'application/configuration'
import { CitiesWithPhone } from 'features/car-details-card/components/CitiesWithPhone'
import { DealerInfo } from 'features/car-details-card/components/DealerInfo'
import { Price } from 'features/car-details-card/components/Price'
import { CarData } from 'features/car/types'
import { Form } from 'features/form/components'
import { getProjectName } from 'lib/get-project-name'
import { createMediaStyles } from 'lib/styled-helpers'
import React, { FC, MutableRefObject, useState } from 'react'
import { useCarDetailsCardConfig } from 'stages/car-details'
import { FormContextProvider } from 'stages/form'
import styled, { useTheme } from 'styled-components'
import { AdditionalEquipment } from 'ui/additional-equipment'
import { Button } from 'ui/button'
import { Icon } from 'ui/icon'
import { LabeledIcon } from 'ui/labeled-icon'

import { getFromStorage, setToStorage } from '../../../lib/storage'
import { useLoopCallback } from '../../../lib/use-loop-callback'
import { EcomIconsId } from '../../../ui/icon/types'
import { ModalWindow } from '../../../ui/modal-window'
import { Slide, Slider } from '../../../ui/slider'
import { Switch } from '../../../ui/switch'
import { MoreInfoContent } from './ModalInfoContent'

export const Description: FC<{
  carData: CarData
  forSmartContract: boolean
  CTAButtonRef: MutableRefObject<HTMLButtonElement | null>
  onCTAButtonCLick: () => void
  tradeInDiscountSwitch: boolean
  setTradeInDiscountSwitch: (value: boolean) => void
  creditDiscountSwitch: boolean
  setCreditDiscountSwitch: (value: boolean) => void
  insuranceDiscountSwitch: boolean
  setInsuranceDiscountSwitch: (value: boolean) => void
  setShowContactManagerPopup?: (value: boolean) => void
}> = ({
  carData,
  forSmartContract,
  CTAButtonRef,
  onCTAButtonCLick,
  tradeInDiscountSwitch,
  setTradeInDiscountSwitch,
  creditDiscountSwitch,
  setCreditDiscountSwitch,
  insuranceDiscountSwitch,
  setInsuranceDiscountSwitch,
  setShowContactManagerPopup
}) => {
  const {
    carViewSettings,
    showColorsInDescriptionOnly,
    callTracking,
    reservationButton,
    baseCarModel
  } = useCarDetailsCardConfig()

  const {
    price,
    priceWithDiscounts,
    year,
    vin,
    title,
    dealers,
    baseSpecifications,
    location,
    locationPhone,
    status,
    marketingColors,
    id
  } = carData

  const theme = useTheme()
  const { disclaimer, noPriceText, hidePrices } = carViewSettings.price
  const CTAButtonText = useAppConfig().smartContract.i18n.detailCardCTA
  const pricesAreNull = price === null && priceWithDiscounts === null
  const [modalTradeInInfo, setModalTradeInInfo] = useState<boolean>(false)
  const [modalCreditInfo, setModalCreditInfo] = useState<boolean>(false)

  const handleCTAButtonClick = () => {
    document.dispatchEvent(new CustomEvent('kodix:ecom:bookButtonClicked', { detail: { carId: id } }))
    document.dispatchEvent(new CustomEvent('kodix:ecom:reservationCar', { detail: { carId: id } }))

    if (getProjectName() === 'avito') {
      onCTAButtonCLick()
    } else {
      if (reservationButton && !reservationButton.link) {
        onCTAButtonCLick()
      }
    }
  }

  const handleContactManagerButtonClicked = () => {
    setShowContactManagerPopup && setShowContactManagerPopup(true)
    document.dispatchEvent(new CustomEvent('kodix:ecom:contactManagerButtonClicked', { detail: { carId: id } }))
  }

  const { gallery, infoBlockDetail } = useCarDetailsCardConfig()
  const [activeSlide, setActiveSlide] = useState(0)
  const [showPopup, setShowPopup] = useState(false)
  const [titleModal, setTitleModal] = useState('')
  const [textModal, setTextModal] = useState('')

  const setActiveSlideCallback = useLoopCallback((index) => () => setActiveSlide(index), [])

  const endSelecting = () => setShowPopup(false)
  const startSelecting = (titleModal: string, textModal: string) => {
    setShowPopup(true)
    setTitleModal(titleModal)
    setTextModal(textModal)
  }

  const setDiscountSettingsToStorageFx = (discountName: string, discountValue: boolean) => {
    let discountSettings = getFromStorage('discountSettings')
    discountSettings[id][discountName] = discountValue
    setToStorage('discountSettings', discountSettings)
  }

  const cardTitleTag = carViewSettings?.titleTag || 'div'
  const CardTitleWithTag = styled(Title.withComponent(cardTitleTag))``

  return (
    <>
      <ModalWindow visible={showPopup} onCloseRequest={endSelecting}>
        <TextModalTitle>{titleModal}</TextModalTitle>
        <Text>{textModal}</Text>
        <Button size={'medium'} onClick={() => endSelecting()}>
          Закрыть
        </Button>
      </ModalWindow>
      <ModalWindow onCloseRequest={() => setModalTradeInInfo(false)} visible={modalTradeInInfo}>
        <TitleModal>Скидка за трейд-ин</TitleModal>
        <SubtitleModal>
          При покупке в трейд-ин вы можете получить дополнительную скидку, а стоимость старого авто пойдёт в зачёт
          нового.
        </SubtitleModal>

        <SubtitleModal>
          {`Размер скидки зависит от стоимости автомобиля и от
          ${getProjectName() === 'mercedes-benz' ? 'центров продаж' : 'дилеров'} —
          они могут назначать разные цены на одинаковые марки и модели.`}
        </SubtitleModal>

        <SubtitleModal>Забронируйте автомобиль, чтобы узнать условия предоставления скидок.</SubtitleModal>
      </ModalWindow>
      <ModalWindow onCloseRequest={() => setModalCreditInfo(false)} visible={modalCreditInfo}>
        <TitleModal>Скидка за кредит</TitleModal>
        <SubtitleModal>
          {`Размер скидки зависит от стоимости автомобиля и от
          ${getProjectName() === 'mercedes-benz' ? 'центров продаж' : 'дилеров'} —
          они могут назначать разные цены на одинаковые марки и модели.`}
        </SubtitleModal>

        <SubtitleModal>Забронируйте автомобиль, чтобы узнать условия предоставления скидок.</SubtitleModal>
      </ModalWindow>
      <CardTitleWithTag>{title}</CardTitleWithTag>
      <Subtitle>
        <SubtitleText>{getProjectName() !== 'avito' && year}</SubtitleText>
        <SubtitleText>{vin}</SubtitleText>
      </Subtitle>
      <SpecificationsWrapper>
        {baseSpecifications.map(({ icon, text }, index) => {
          return (
            <Specification key={index}>
              <LabeledIcon
                text={text}
                icon={icon}
                size={'normal'}
                iconSize={getProjectName() === 'generic_v2' ? 'small' : 'small'}
              />
            </Specification>
          )
        })}
      </SpecificationsWrapper>
      <ColorsWrapper showColorsInDescriptionOnly={showColorsInDescriptionOnly}>
        {marketingColors.map(({ colorPrimary, colorSecondary, heading, colorName }, index) => {
          return (
            <ColorInfo key={index}>
              <ColorField colorPrimary={colorPrimary} colorSecondary={colorSecondary} />
              <ColorDescription>
                {heading}: {colorName}
              </ColorDescription>
            </ColorInfo>
          )
        })}
      </ColorsWrapper>
      {getProjectName() === 'avito' && !forSmartContract && (
        <>
          {baseCarModel.tradeinDiscount > 0 && (
            <SwitchWrapper>
              <Switch
                mobileSpaceBetween={true}
                onChange={() => {
                  setTradeInDiscountSwitch(!tradeInDiscountSwitch)
                  setDiscountSettingsToStorageFx('tradeInDiscount', !tradeInDiscountSwitch)
                }}
                size={'big'}
                checked={tradeInDiscountSwitch}
              />

              <Label>
                <LabelTitle>Трейд-ин</LabelTitle>
                <LabelText>
                  Скидка {baseCarModel.tradeinDiscount} ₽{' '}
                  <a href="javascript:void(0)" onClick={() => setModalTradeInInfo(!modalTradeInInfo)}>
                    Подробнее
                  </a>
                </LabelText>
              </Label>
            </SwitchWrapper>
          )}

          {baseCarModel.creditDiscount > 0 && (
            <SwitchWrapper>
              <Switch
                mobileSpaceBetween={true}
                onChange={() => {
                  setCreditDiscountSwitch(!creditDiscountSwitch)
                  setDiscountSettingsToStorageFx('creditDiscount', !creditDiscountSwitch)
                }}
                size={'big'}
                checked={creditDiscountSwitch}
              />

              <Label>
                <LabelTitle>Кредит</LabelTitle>
                <LabelText>
                  Скидка {baseCarModel.creditDiscount} ₽{' '}
                  <a href="javascript:void(0)" onClick={() => setModalCreditInfo(!modalCreditInfo)}>
                    Подробнее
                  </a>
                </LabelText>
              </Label>
            </SwitchWrapper>
          )}

          {baseCarModel.insuranceDiscount > 0 && (
            <SwitchWrapper>
              <Switch
                mobileSpaceBetween={true}
                onChange={() => {
                  setInsuranceDiscountSwitch(!insuranceDiscountSwitch)
                  setDiscountSettingsToStorageFx('insuranceDiscount', !insuranceDiscountSwitch)
                }}
                size={'big'}
                checked={insuranceDiscountSwitch}
              />

              <Label>
                <LabelTitle>Каско</LabelTitle>
                <LabelText>
                  Скидка {baseCarModel.insuranceDiscount} ₽ <a href="javascript:void(0)">Подробнее</a>
                </LabelText>
              </Label>
            </SwitchWrapper>
          )}
        </>
      )}
      {forSmartContract && <DealerInfo dealers={dealers} status={status} callTracking={callTracking} />}
      {!forSmartContract && (
        <>
          <PricesWrapper>
            {!pricesAreNull && !hidePrices && (
              <Price
                higher={price}
                lower={priceWithDiscounts}
                disclaimer={carData.available ? disclaimer : undefined}
                showRange={carViewSettings.price.showAsRange}
                alwaysForceRange={carViewSettings.price.alwaysForceRange}
                creditDiscountSwitch={creditDiscountSwitch}
                tradeInDiscountSwitch={tradeInDiscountSwitch}
                insuranceDiscountSwitch={insuranceDiscountSwitch}
              />
            )}
          </PricesWrapper>

          {getProjectName() === 'nissan' && <AdditionalEquipment carData={carData} carViewSettings={carViewSettings} />}

          {carData.available && (
            <CTAWrapper>
              <Button
                ref={CTAButtonRef}
                block
                size={getProjectName() === 'volkswagen' ? 'small' : 'medium'}
                style={{ display: 'none', marginBottom: theme.spacing[1] }}
                className="open_smart_contract"
              >
                {CTAButtonText}
              </Button>
              {getProjectName() === 'avito' && (
                <ContactManager>
                  <Button block={true} size="medium" onClick={() => handleContactManagerButtonClicked()}>
                    Связаться с менеджером
                  </Button>
                </ContactManager>
              )}
              {reservationButton && (
                <Button
                  link={reservationButton.link}
                  block={true}
                  size={getProjectName() === 'volkswagen' ? 'small' : 'medium'}
                  onClick={() => handleCTAButtonClick()}
                  background="#9D48FF"
                >
                  {pricesAreNull && !hidePrices && noPriceText ? noPriceText : reservationButton.text}
                </Button>
              )}
              {getProjectName() === 'avito' && (
                <InfoText>Забронируйте автомобиль, чтобы он вас дождался. Сейчас вы ни за что не платите.</InfoText>
              )}
            </CTAWrapper>
          )}

          {getProjectName() === 'avito' && infoBlockDetail && (
            <Advantage>
              <TitleAdvantage>Преимущества</TitleAdvantage>
              <Thumbs>
                <Slider
                  slidesPerPage={3}
                  activeGroup={activeSlide}
                  slidesPerGroup={1}
                  showArrows={false}
                  paddingForOverflow={'35px 0 0'}
                >
                  {infoBlockDetail.map((item: { text: string; iconId: EcomIconsId; textModal: string }, index) => (
                    <Slide key={index} onClick={setActiveSlideCallback(0)}>
                      <ThumbContent isActive>
                        <InfoBlockItem onClick={() => startSelecting(item.text, item.textModal)}>
                          <Icon name={item.iconId} size={0.75} />
                          <InfoBlockText>{item.text}</InfoBlockText>
                        </InfoBlockItem>
                      </ThumbContent>
                    </Slide>
                  ))}
                </Slider>
              </Thumbs>
            </Advantage>
          )}

          <DealerInfo
            showLocationAndPhone={getProjectName() !== 'avito' && getProjectName() !== 'nissan'}
            location={location}
            dealers={dealers}
            status={status}
            callTracking={callTracking}
            locationPhone={locationPhone}
          />
        </>
      )}
      {getProjectName() === 'nissan' && (
        <CitiesWithPhone
          locationPhone={carData.locationPhone}
          locationLength={carData.locationsLength}
          location={carData.location}
          callTracking={callTracking}
        />
      )}
    </>
  )
}

const CTAWrapper = styled.div`
  margin-top: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '15px'
    return theme.spacing[5]
  }};

  .open_smart_contract {
    background-color: ${({ theme }) => {
      if (getProjectName() === 'avito') return '#9D48FF'
      if (getProjectName() === 'generic_v2') return 'transparent'
      return theme.palette.brand.primary
    }};
    animation: ${() => getProjectName() === 'generic_v2' && 'none'};
    box-shadow: ${() => getProjectName() === 'generic_v2' && 'none'};
    border: ${({ theme }) => getProjectName() === 'generic_v2' && `1px solid ${theme.palette.grey[100]}`};
    border-radius: ${({ theme }) => getProjectName() === 'generic_v2' && `100px`};
    height: ${() => getProjectName() === 'generic_v2' && `40px`};
    text-transform: ${() => getProjectName() === 'generic_v2' && `uppercase`};
    font-weight: ${() => getProjectName() === 'generic_v2' && `500`};
    margin-bottom: 10px !important;

    &:hover {
      background-color: ${({ theme }) => {
        if (getProjectName() === 'haval') return 'rgb(0, 202, 191)'
        if (getProjectName() === 'generic_v2') return theme.palette.grey[900]
        return theme.palette.brand.secondary
      }};
    }

    @media screen and (max-width: 1023px) and (min-width: 768px) {
      margin-bottom: ${() => getProjectName() === 'generic_v2' && '0!important'};
    }
  }

  .kdxsc-attention {
    background: ${({ theme }) => getProjectName() === 'generic_v2' && theme.palette.grey[900]};
    border-radius: ${({ theme }) => getProjectName() === 'generic_v2' && '8px'};
    font-family: ${() =>
      getProjectName() === 'generic_v2' &&
      `Manrope, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif,
      'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`};
  }

  .kdxsc-attention::after {
    content: ${({ theme }) => getProjectName() === 'generic_v2' && 'none'};
  }

  .kdxsc-attention__header,
  .kdxsc-attention-body-list__item {
    color: ${({ theme }) => getProjectName() === 'generic_v2' && theme.palette.grey[600]};
  }

  .kdxsc-attention-footer-video__text {
    color: ${({ theme }) => getProjectName() === 'generic_v2' && theme.palette.grey[400]};

    &:hover {
      color: ${({ theme }) => getProjectName() === 'generic_v2' && theme.palette.grey[400]};
    }
  }

  .kdxsc-attention__body {
    color: ${({ theme }) => getProjectName() === 'generic_v2' && theme.palette.grey[600]};
  }

  .kdxsc-attention-footer__button {
    border: 1px solid ${({ theme }) => getProjectName() === 'generic_v2' && theme.palette.grey[100]};
    border-radius: ${() => getProjectName() === 'generic_v2' && '100px'};
    overflow: ${() => getProjectName() === 'generic_v2' && 'hidden'};
    background: ${() => getProjectName() === 'generic_v2' && 'none'};
    text-align: ${() => getProjectName() === 'generic_v2' && 'center'};

    @media screen and (min-width: 1200px) and (max-width: 1350px) and (max-width: 450px) {
      margin-top: ${() => getProjectName() === 'generic_v2' && '10px'};
    }
  }

  .kdxsc-attention-close {
    background: ${() => getProjectName() === 'generic_v2' && 'none'};
    padding: ${() => getProjectName() === 'generic_v2' && '5px 12px'};
    font-size: ${() => getProjectName() === 'generic_v2' && '14px'};
  }

  .kdxsc-attention-close:hover {
    background: ${({ theme }) => getProjectName() === 'generic_v2' && theme.palette.grey[900]};
  }

  .kdxsc-attention-footer-video__img {
    ${() =>
      getProjectName() === 'generic_v2' &&
      `
        width:  35px;
        height: 35px;
        flex-shrink: 0;
        background-image: \`url("data:image/svg+xml,%3Csvg width='35' height='35' viewBox='0 0 35 35' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='35' height='35' rx='17.5' fill='%231F1F1F'/%3E%3Cpath d='M24 16.634C24.6667 17.0189 24.6667 17.9811 24 18.366L15 23.5622C14.3333 23.9471 13.5 23.466 13.5 22.6962V12.3038C13.5 11.534 14.3333 11.0529 15 11.4378L24 16.634Z' fill='%23D9D9D9'/%3E%3C/svg%3E%0A");\`
    `}
  }

  .kdxsc-attention-footer__video {
    ${() =>
      getProjectName() === 'generic_v2' &&
      `
      @media screen and (min-width: 1200px) and (max-width: 1350px) {
          margin-bottom: 5px;
        }
      @media screen and  (max-width: 450px) {
          margin-bottom: 5px;
        }
    `}
  }

  .kdxsc-attention__footer {
    ${() =>
      getProjectName() === 'generic_v2' &&
      `
        @media screen and (min-width: 1200px) and (max-width: 1350px) {
          flex-direction: column;
        }
        
         @media screen and  (max-width: 450px) {
          flex-direction: column;
        }
    `}
  }

  @media screen and (max-width: 1023px) and (min-width: 768px) {
    display: ${() => getProjectName() === 'generic_v2' && `flex`};
    justify-content: ${() => getProjectName() === 'generic_v2' && `space-between`};

    button,
    a {
      max-width: ${() => getProjectName() === 'generic_v2' && `calc(50% - 5px)`};
      width: ${() => getProjectName() === 'generic_v2' && `100%`};
    }
  }
`

const PricesWrapper = styled.div`
  position: relative;
  margin: ${({ theme }) => {
    if (getProjectName() === 'avito') return `${theme.spacing[6]} ${theme.spacing[0]} ${theme.spacing[8]}`
    if (getProjectName() === 'generic_v2') return `${theme.spacing[2]} ${theme.spacing[0]} ${theme.spacing[2]}`
    return `${theme.spacing[8]} ${theme.spacing[0]}`
  }};
  display: flex;
  flex-direction: ${() => (getProjectName() === 'avito' ? 'column-reverse' : 'column')};
`

const Title = styled.div`
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  ${({ theme }) => (getProjectName() === 'avito' ? theme.textStyles.h3 : theme.textStyles.h5)};
  font-size: ${() => getProjectName() === 'haval' && '2rem'};
  font-weight: ${({ theme }) => {
    if (getProjectName() === 'avito') return 800
    if (getProjectName() === 'haval') return 500
    if (getProjectName() === 'benda') return 400
    if (getProjectName() === 'generic_v2') return 600
    return 300
  }};
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return theme.palette.grey[500]
    if (getProjectName() === 'generic_v2') return theme.palette.grey[100]
    return theme.palette.grey[900]
  }};
  text-transform: ${({ theme }) => getProjectName() === 'generic_v2' && 'uppercase'};

  @media screen and (max-width: 767px) {
    font-size: ${() => getProjectName() === 'generic_v2' && '16px'};
  }
`

const Subtitle = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return theme.spacing[3]
    return theme.spacing[5]
  }};
  ${() => getProjectName() === 'avito' && 'flex-direction: row-reverse;'};
`

const SubtitleText = styled.span`
  color: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return theme.palette.grey[600]
    return theme.palette.grey[700]
  }};
  ${({ theme }) => theme.textStyles.p2}

  font-size: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '1.4rem'
    return '1.6rem'
  }};

  @media screen and (max-width: 767px) {
    font-size: ${() => getProjectName() === 'generic_v2' && '12px'};
  }
`

const SpecificationsWrapper = styled.div`
  display: flex;

  ${createMediaStyles(() => ({
    phone: 'flex-direction: column',
    tablet: 'flex-direction: row',
    desktop: 'flex-direction: column'
  }))}
`

const Specification = styled.div`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    ${({ theme }) =>
      createMediaStyles(() => ({
        phone: `margin-bottom: ${theme.spacing[2]}`,
        tablet: `margin-right: ${theme.spacing[5]}`,
        desktop: `margin-bottom: ${theme.spacing[2]}`
      }))};

    ${() =>
      getProjectName() === 'generic_v2' &&
      `
      @media screen and (max-width: 767px) {
        margin-bottom: 5px;
      }
    `}
  }

  ${() =>
    getProjectName() === 'generic_v2' &&
    `
    margin-bottom: 0!important;
    
    @media screen and (max-width: 767px) {
    margin-bottom: 5px!important;
    }
    `}
`

const ColorsWrapper = styled.div<{ showColorsInDescriptionOnly: boolean }>`
  margin-top: ${({ theme }) => theme.spacing[5]};
  flex-direction: column;

  ${({ showColorsInDescriptionOnly }) =>
    showColorsInDescriptionOnly
      ? createMediaStyles(() => ({
          phone: 'display: flex;',
          tablet: 'display: flex;',
          desktop: 'display: flex;',
          projectors: 'display: flex;'
        }))
      : createMediaStyles(() => {
          if (getProjectName() === 'generic_v2') {
            return {
              phone: 'display: none;',
              tablet: 'display: none;',
              desktop: 'display: none;',
              projectors: 'display: none;'
            }
          }
          return {
            phone: 'display: flex;',
            tablet: 'display: none;',
            desktop: 'display: flex;',
            projectors: 'display: none;'
          }
        })};
`

const ColorInfo = styled.div`
  display: flex;
  align-items: ${() => (getProjectName() === 'avito' ? 'flex-end' : 'flex-start')};
  margin-top: ${({ theme }) => (getProjectName() === 'avito' ? '' : theme.spacing[1])};

  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing[1]};
  }
`

const ColorField = styled.div<{ colorPrimary: string; colorSecondary?: string }>`
  flex-shrink: 0;
  width: ${({ colorSecondary, theme }) => (colorSecondary ? theme.spacing[4] : `calc(${theme.spacing[4]} + 1px)`)};
  height: ${({ colorSecondary, theme }) => (colorSecondary ? theme.spacing[4] : `calc(${theme.spacing[4]} + 1px)`)};
  margin-right: ${({ theme }) => theme.spacing[2]};
  border-radius: 50%;
  background: ${({ colorPrimary }) => colorPrimary};
  box-shadow: ${({ colorSecondary }) =>
    getProjectName() === 'avito' ? '0px 0px 2px 1.5px rgb(34 60 80 / 40%)' : `0 0 0 1px ${colorSecondary}`};
`

const ColorDescription = styled.div`
  margin: -2px 0 0 0;
  ${({ theme }) => theme.textStyles.p2};
  line-height: 20px;
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return theme.palette.grey[500]
    if (getProjectName() === 'generic_v2') return theme.palette.grey[100]
    return
  }};

  font-size: ${() => {
    if (getProjectName() === 'generic_v2') return '1.2rem'
    return '1.6rem'
  }};
`

const InfoBlockItem = styled.div`
  background: #f3f2f1;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border-radius: ${() => (getProjectName() === 'avito' ? '16px' : '6px')};
  margin: 0 8px 8px 0;
  cursor: pointer;

  ${() =>
    createMediaStyles(() => ({
      phone: 'min-height: 32px; padding: 12px 8px; flex-direction: column;',
      tablet: 'padding: 12px 8px; flex-direction: column;'
    }))}
  &:last-child {
    margin-right: 0;
  }
`

const InfoBlockText = styled.div`
  line-height: 20px;
  text-align: center;
  ${() =>
    createMediaStyles(() => ({
      phone: `font-size: 14px; line-height: normal; margin-left: 5px;`,
      tablet: `font-size: 16px; max-width: 80%; margin-left: 0; margin-top: 5px;`,
      desktop: `font-size: 14px; line-height: normal;`,
      projectors: `font-size: 16px`
    }))}
`

const TextModalTitle = styled.div`
  ${({ theme }) => theme.typography.title.h3};
  font-weight: bold;
  margin: 5px;
`

const Text = styled.div`
  ${({ theme }) => theme.typography.paragraph.p1};
  font-weight: 500;
  margin: 0 0 25px 0;
`

const Thumbs = styled.div`
  margin-top: ${({ theme }) => theme.spacing[6]};
  width: 90vw;

  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `margin-right: -${theme.spacing[1]}; margin-left: -${theme.spacing[1]};`,
      desktop: `margin-right: -${theme.spacing[3]}; margin-left: -${theme.spacing[3]};`
    }))}
`

const ThumbContent = styled.div<{ isActive: boolean }>`
  opacity: ${({ isActive }) => (isActive ? '1' : '0.5')};
  cursor: pointer;

  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `padding-right: ${theme.spacing[1]}; padding-left: ${theme.spacing[1]};`,
      desktop: `padding-right: ${theme.spacing[3]}; padding-left: ${theme.spacing[3]};`
    }))}
`

const TitleAdvantage = styled.div`
  margin-top: 4.8rem;
  ${({ theme }) => theme.textStyles.h4};
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `max-width: none; margin-bottom: 10px;`,
      tablet: `max-width: 75%; margin-bottom: ${theme.spacing[4]};`,
      desktop: 'max-width: none',
      projectors: 'max-width: 75%'
    }))}
`

const Advantage = styled.div`
  display: none;
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `display:block`,
      desktop: 'display: none'
    }))}
`

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  margin-top: 23px;
  border-left: 1px solid ${(props) => props.theme.palette.grey[100]};
  border: 1px solid ${(props) => (getProjectName() === 'avito' ? 0 : props.theme.palette.grey[100])};
  ${createMediaStyles(() => ({
    phone: 'flex-direction: row-reverse; justify-content: space-between;border-width:1px 0 0',
    tablet: `flex-direction: row; justify-content: stretch;min-width: 20rem; border-width: ${() =>
      getProjectName() === 'avito' ? 0 : '1px 0 0 1px'}`,
    desktop: `border-width: ${() => (getProjectName() === 'avito' ? 0 : '0px 0 0 1px')}`
  }))}
`

const Label = styled.p`
  margin: 0;
  ${({ theme }) => theme.textStyles.p2};
`

const LabelTitle = styled.div`
  ${({ theme }) => theme.textStyles.p1}
  font-weight: 800;
`

const LabelText = styled.div`
  ${({ theme }) => theme.textStyles.p2};

  a {
    color: inherit;
  }
`

const TitleModal = styled.div`
  ${({ theme }) => theme.textStyles.h4};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  max-width: 75%;
`

const SubtitleModal = styled.div`
  ${({ theme }) => theme.textStyles.p2};
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.black};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`

const ContactManager = styled.div`
  margin-bottom: 8px;
`

const InfoText = styled.div`
  ${({ theme }) => theme.textStyles.p2}
  color: ${({ theme }) => theme.palette.grey[700]};
  font-weight: 400;
  margin-top: 12px;
`
